// screens/staff/StaffDashboardLayout.tsx
//
// W6 v1 Session 1 — desktop-first layout for the instructor surface.
//
// Structure:
//   ┌─ TopBar: school name + staff name/role + logout ─────────────────────┐
//   │ ┌─ SideNav (role-filtered) ─┬─ <main> {children} ─────────────────┐  │
//   │ │ Studenti                  │                                      │  │
//   │ │ Codici invito             │                                      │  │
//   │ │ Frequenze                 │                                      │  │
//   │ └────────────────────────────┴─────────────────────────────────────┘  │
//   └────────────────────────────────────────────────────────────────────────┘
//
// The SideNav hides surfaces the active role can't access (e.g. instructors
// don't see the Codici tab in Session 1; that arrives in Session 2).
//
// Responsive: desktop-first surface. Components remain functional at 768px+
// via `1fr` grid-column compression in the data tables, but there is no
// explicit responsive collapse — no @media query, no matchMedia listener.
// Flagged for v1.1 if staff use the dashboard on tablets at the school front desk.
//
// Voice: formal Italian ("Lei"/"voi"). NEVER "tu" — that's the student app.

import { useState, useEffect, useRef, type ReactNode, type JSX } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTokens } from '../../state/theme';
import { staffApi, staffAuthApi, type StaffMe } from '../../api/staff';
import { brandLabel } from './brandLabel';

interface NavItem {
  to:    string;
  label: string;
  // Roles that can SEE this nav item. (Server still enforces.)
  allow: ReadonlyArray<StaffMe['role']>;
  testid?: string;
  /** When set, the layout fetches a pending-count number on mount and
   *  renders a small accent-colored pill next to the label. Only shown
   *  when count > 0. */
  badge?: 'bozze-pending';
}

const NAV_ITEMS: ReadonlyArray<NavItem> = [
  // W6 v1 Session 2 — "Pannello" lands on /staff/home (6-card dashboard).
  // Only roles with pedagogical visibility see it; segreteria stays out
  // (the BE 403s the underlying endpoint, but the link is hidden anyway).
  { to: '/staff/home',      label: 'Pannello',       allow: ['titolare', 'instructor_teoria', 'instructor_guida'], testid: 'staff-nav-home' },
  { to: '/staff/studenti',  label: 'Studenti',       allow: ['titolare', 'instructor_teoria', 'instructor_guida', 'segreteria'], testid: 'staff-nav-studenti' },
  // W6 v1 Session 3 — "Lacune" + "Metriche" land for pedagogy-cleared roles.
  { to: '/staff/lacune',    label: 'Lacune',         allow: ['titolare', 'instructor_teoria', 'instructor_guida'], testid: 'staff-nav-lacune' },
  { to: '/staff/feedback-prof', label: 'Feedback Prof', allow: ['titolare', 'instructor_teoria', 'instructor_guida'], testid: 'staff-nav-prof-feedback' },
  // W7 Session 2 — "Bozze" (corpus draft review queue). Pedagogy-roles only.
  // Segreteria is intentionally excluded — corpus is curriculum-team-shaped.
  // Badge: count of pending drafts; hidden when count = 0.
  { to: '/staff/bozze',     label: 'Bozze',          allow: ['titolare', 'instructor_teoria', 'instructor_guida'], testid: 'staff-nav-bozze',     badge: 'bozze-pending' },
  // W6 v1.1 — "Polso classe" (spec §5.5). Pedagogy-roles only.
  { to: '/staff/pulse',     label: 'Polso classe',   allow: ['titolare', 'instructor_teoria', 'instructor_guida'], testid: 'staff-nav-pulse' },
  { to: '/staff/metriche',  label: 'Metriche',       allow: ['titolare', 'instructor_teoria', 'instructor_guida'], testid: 'staff-nav-metriche' },
  { to: '/staff/codici',    label: 'Codici invito',  allow: ['titolare', 'segreteria'], testid: 'staff-nav-codici' },
  { to: '/staff/frequenze', label: 'Frequenze',      allow: ['titolare', 'instructor_teoria', 'instructor_guida', 'segreteria'], testid: 'staff-nav-frequenze' },
];

const ROLE_LABEL: Record<StaffMe['role'], string> = {
  titolare:          'Titolare',
  instructor_teoria: 'Istruttore di teoria',
  instructor_guida:  'Istruttore di guida',
  segreteria:        'Segreteria',
};

// `brandLabel(role)` lives in `./brandLabel.ts` so it can be shared with
// the dashboard-home page heading (W6 v1.1 Session 3 polish item 6) without
// tripping React Refresh's `only-export-components` rule on this file.

export function StaffDashboardLayout({
  children,
  me: meProp,
  onMeChange,
}: {
  children: ReactNode;
  me?: StaffMe | null;
  onMeChange?: (me: StaffMe | null) => void;
}): JSX.Element {
  const tokens = useTokens();
  const navigate = useNavigate();
  const [me, setMe] = useState<StaffMe | null>(meProp ?? null);
  const [loading, setLoading] = useState<boolean>(meProp == null);
  const [error, setError] = useState<string | null>(null);
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const logoutPending = useRef(false);
  // W7 S2 — pending-count badge for the "Bozze" sidenav link. Fetched
  // once on mount when the active role is allowed to see the link.
  // Refreshes are triggered by the BozzeQueueScreen via window event;
  // a pending=0 result just hides the badge.
  const [bozzePendingCount, setBozzePendingCount] = useState<number>(0);

  useEffect(() => {
    if (meProp) {
      setMe(meProp);
      setLoading(false);
      return;
    }
    let alive = true;
    staffApi.me()
      .then((m) => {
        if (!alive) return;
        setMe(m);
        onMeChange?.(m);
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setError('Sessione scaduta. Effettui di nuovo l\'accesso.');
        setLoading(false);
      });
    return () => { alive = false; };
  }, [meProp, onMeChange]);

  // Fetch the bozze pending count for roles that see the link.
  // Failure is silent — the badge just doesn't render.
  // BozzeQueueScreen dispatches `bozze:pending-count-changed` on every
  // approve / reject; we re-fetch the count then so the sidenav badge stays
  // in lock-step with the queue. (Pre-fix: badge could read "5" while the
  // queue had 0 left — it only refreshed on full page reload.)
  useEffect(() => {
    if (!me) return;
    if (!['titolare', 'instructor_teoria', 'instructor_guida'].includes(me.role)) {
      return;
    }
    let alive = true;
    function fetchPendingCount() {
      staffApi.draftPendingCount()
        .then((res) => { if (alive) setBozzePendingCount(res.pending_count); })
        .catch(() => { /* silent */ });
    }
    fetchPendingCount();
    function handler() {
      fetchPendingCount();
    }
    window.addEventListener('bozze:pending-count-changed', handler);
    return () => {
      alive = false;
      window.removeEventListener('bozze:pending-count-changed', handler);
    };
  }, [me]);

  async function handleLogout() {
    if (logoutPending.current) return;
    logoutPending.current = true;
    setLoggingOut(true);
    setLogoutError(null);
    try {
      const completedCurrentSession = await staffAuthApi.logout();
      if (!completedCurrentSession) return;
      onMeChange?.(null);
      navigate('/staff/login', { replace: true });
    } catch {
      setLogoutError('Non è stato possibile confermare l’uscita. Riprovi.');
    } finally {
      logoutPending.current = false;
      setLoggingOut(false);
    }
  }

  if (loading) {
    return (
      <div data-testid="staff-loading" style={{ minHeight: '100vh', background: tokens.bg, color: tokens.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: tokens.fonts.body }}>
        Caricamento…
      </div>
    );
  }
  if (error || !me) {
    return (
      <div data-testid="staff-error" style={{ minHeight: '100vh', background: tokens.bg, color: tokens.text, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: tokens.fonts.body, gap: 16 }}>
        <div role="alert" style={{ color: tokens.wrong }}>{logoutError ?? error ?? 'Errore di caricamento.'}</div>
        <button onClick={handleLogout} disabled={loggingOut} style={{ background: tokens.accent, color: tokens.mode === 'dark' ? '#1a1510' : '#fff', border: 'none', borderRadius: tokens.radius.pill, padding: '10px 20px', cursor: 'pointer' }}>
          {loggingOut ? 'Uscita…' : "Torna all'accesso"}
        </button>
      </div>
    );
  }

  const visibleNav = NAV_ITEMS.filter(item => item.allow.includes(me.role));

  return (
    <div
      data-testid="staff-dashboard-layout"
      style={{
        minHeight:     '100vh',
        background:    tokens.bg,
        color:         tokens.text,
        fontFamily:    tokens.fonts.body,
        display:       'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top bar */}
      <header
        style={{
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
          padding:        '14px 24px',
          borderBottom:   `1px solid ${tokens.line}`,
          background:     tokens.surface,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div data-testid="staff-topbar-brand" style={{ fontFamily: tokens.fonts.serif, fontSize: 18, fontWeight: 700, letterSpacing: -0.3 }}>
            {brandLabel(me.role)}
          </div>
          <div style={{ fontSize: 12, color: tokens.textMuted }}>
            {me.school_name ?? 'Autoscuola'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              {me.first_name}{me.last_name ? ` ${me.last_name}` : ''}
            </div>
            <div style={{ fontSize: 12, color: tokens.textMuted }}>{ROLE_LABEL[me.role]}</div>
          </div>
          <button
            onClick={handleLogout}
            disabled={loggingOut}
            data-testid="staff-logout"
            style={{
              background:   'transparent',
              color:        tokens.textMuted,
              border:       `1px solid ${tokens.line}`,
              borderRadius: tokens.radius.md,
              padding:      '8px 14px',
              cursor:       'pointer',
              fontSize:     13,
            }}
          >
            {loggingOut ? 'Uscita…' : 'Esci'}
          </button>
        </div>
      </header>
      {logoutError && <div role="alert" style={{ color: tokens.wrong, padding: '12px 24px' }}>{logoutError}</div>}

      {/* Main = sidenav + content. Row layout at all widths (no responsive
          collapse — see header comment). */}
      <div
        style={{
          flex:    1,
          display: 'flex',
          flexDirection: 'row',
          minHeight: 0,
        }}
      >
        <nav
          aria-label="Navigazione principale"
          style={{
            background:   tokens.drawerBg,
            borderRight:  `1px solid ${tokens.line}`,
            padding:      '20px 12px',
            minWidth:     200,
            display:      'flex',
            flexDirection:'column',
            gap:          4,
          }}
        >
          {visibleNav.map((item) => {
            const showBadge = item.badge === 'bozze-pending' && bozzePendingCount > 0;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                style={({ isActive }) => ({
                  display:        'flex',
                  alignItems:     'center',
                  justifyContent: 'space-between',
                  gap:            8,
                  padding:        '10px 14px',
                  borderRadius:   tokens.radius.md,
                  color:          isActive ? tokens.accentInk : tokens.text,
                  background:     isActive ? tokens.accentSoft : 'transparent',
                  fontSize:       14,
                  fontWeight:     isActive ? 600 : 500,
                  textDecoration: 'none',
                })}
              >
                <span>{item.label}</span>
                {showBadge && (
                  <span
                    data-testid={`${item.testid}-badge`}
                    aria-label={`${bozzePendingCount} bozze in attesa`}
                    style={{
                      background:    tokens.accent,
                      color:         tokens.mode === 'dark' ? '#1a1510' : '#fff',
                      borderRadius:  tokens.radius.pill,
                      padding:       '1px 8px',
                      fontSize:      11,
                      fontWeight:    700,
                      minWidth:      20,
                      textAlign:     'center',
                    }}
                  >
                    {bozzePendingCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <main style={{ flex: 1, padding: '24px 32px', overflowY: 'auto' }}>
          {children}
        </main>
      </div>
    </div>
  );
}
